import PropTypes from "prop-types";
import "./styles.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRef } from "react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useInView, motion } from "framer-motion";

/* #META
 {
  "NAME": "Clients Testimonials",
  "CLASSNAME": "clients-testimonials-component",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

const Client = ({ data }) => {
  const { client_testominals } = data;
  const { content, testimonials_slider } = client_testominals || {};
  const { label, label_color, wording } = content || {};
  const testimonials = testimonials_slider?.testimonials || [];

  const contentRef = useRef(null);
  const sliderRef = useRef(null);

  const contentInView = useInView(contentRef, { once: true, threshold: 0.3 });
  const sliderInView = useInView(sliderRef, { once: true, threshold: 0.3 });

  return (
    <section className="clients-testimonials-component">
      <div className="container-revamp">
        <div className="flex-container">
          <motion.div
            className="client-testimonials-content"
            ref={contentRef}
            initial={{ opacity: 0, x: -80 }}
            animate={contentInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {label && <p className={`content-label content-label-${label_color}`}>{label}</p>}
            {wording && (
              <div className="content-wording" dangerouslySetInnerHTML={{ __html: wording }} />
            )}
          </motion.div>
          <motion.div
            className="client-testimonials-slider"
            ref={sliderRef}
            initial={{ opacity: 0, x: 80 }}
            animate={sliderInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          >
            <Swiper
              modules={[Pagination, Autoplay]}
              slidesPerView={1}
              spaceBetween={20}
              pagination={{ clickable: true }}
              autoplay={{
                delay: 6000,
                disableOnInteraction: false,
              }}
            >
              {testimonials.map((testimonialItem, index) => {
                const { company_logo, author, position, testimonial } = testimonialItem;
                return (
                  <SwiperSlide key={index}>
                    <div className="testimonial-card">
                      <div className="testimonial-top-row">
                        {company_logo?.url && (
                          <div className="company-logo-wrapper max-w-full">
                            <img
                              src={company_logo.url}
                              alt={company_logo.alt || company_logo.title}
                              className="company-logo w-full max-w-none object-contain"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="testimonial-author">
                          {author && (
                            <span className="author-name">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                              >
                                <g id="vuesax/linear/user-octagon">
                                  <g id="user-octagon">
                                    <path
                                      id="Vector"
                                      d="M14.5538 5.72067V10.2807C14.5538 11.0273 14.1538 11.7207 13.5071 12.1007L9.5471 14.3873C8.90043 14.7607 8.10043 14.7607 7.4471 14.3873L3.4871 12.1007C2.84043 11.7273 2.44043 11.034 2.44043 10.2807V5.72067C2.44043 4.97401 2.84043 4.28065 3.4871 3.90065L7.4471 1.61398C8.09376 1.24065 8.89376 1.24065 9.5471 1.61398L13.5071 3.90065C14.1538 4.28065 14.5538 4.96734 14.5538 5.72067Z"
                                      stroke="#292643"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <g id="Group">
                                      <path
                                        id="Vector_2"
                                        d="M8.49962 7.33321C9.3575 7.33321 10.053 6.63775 10.053 5.77987C10.053 4.92198 9.3575 4.22656 8.49962 4.22656C7.64174 4.22656 6.94629 4.92198 6.94629 5.77987C6.94629 6.63775 7.64174 7.33321 8.49962 7.33321Z"
                                        stroke="#292643"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        id="Vector_3"
                                        d="M11.1663 11.1069C11.1663 9.90689 9.97301 8.93359 8.49967 8.93359C7.02634 8.93359 5.83301 9.90689 5.83301 11.1069"
                                        stroke="#292643"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </g>
                                  </g>
                                </g>
                              </svg>
                              {author}
                            </span>
                          )}
                          {position && <span className="author-position">{position}</span>}
                        </div>
                      </div>
                      <div
                        className="testimonial-content"
                        dangerouslySetInnerHTML={{ __html: testimonial }}
                      />
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

Client.propTypes = {
  data: PropTypes.shape({
    acf_fc_layout: PropTypes.string,
    client_testominals: PropTypes.shape({
      content: PropTypes.shape({
        label: PropTypes.string,
        label_color: PropTypes.string,
        wording: PropTypes.string,
      }),
      testimonials_slider: PropTypes.shape({
        testimonials: PropTypes.arrayOf(
          PropTypes.shape({
            company_logo: PropTypes.shape({
              url: PropTypes.string,
              alt: PropTypes.string,
              title: PropTypes.string,
            }),
            author: PropTypes.string,
            position: PropTypes.string,
            testimonial: PropTypes.string,
          })
        ),
      }),
    }),
  }),
};

export default Client;
